import React, { useEffect, useRef } from 'react';
import { useCmsEdit } from './CmsEditProvider';
import { CmsFieldType } from './protocol';

interface EditableProps {
  keyName: string;
  fieldType: CmsFieldType;
  /** Human label shown in the hover badge; defaults to the key. */
  label?: string;
  /** Current resolved value (used so the admin inspector opens with it). */
  value?: string;
  /** Layout mode of the wrapper in edit mode. Normal mode renders children with no wrapper at all. */
  display?: 'block' | 'inline' | 'inline-block' | 'flex';
  style?: React.CSSProperties;
  children: React.ReactNode;
}

/**
 * Marks a CMS-driven region of the storefront.
 *
 * Normal mode: renders `children` untouched — zero DOM/CSS impact for customers.
 * Edit mode (inside the admin iframe): wraps children, draws a dashed outline
 * + key badge on hover (or always when "highlight all" is on), and reports a
 * click to the admin so the inspector can open that key.
 */
const Editable = ({ keyName, fieldType, label, value, display = 'block', style, children }: EditableProps) => {
  const cms = useCmsEdit();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!cms.enabled) return;
    return cms.registerKey(keyName, fieldType, label);
  }, [cms.enabled, cms.registerKey, keyName, fieldType, label]);

  if (!cms.enabled) return <>{children}</>;

  const selected = cms.selectedKey === keyName;
  const cls = ['cms-editable', selected && 'cms-editable--selected', cms.highlightAll && 'cms-editable--all']
    .filter(Boolean).join(' ');

  const onClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    cms.select(keyName, fieldType, value ?? '', ref.current);
  };

  return (
    <div
      ref={ref}
      className={cls}
      style={{ display, ...style }}
      data-cms-key={keyName}
      data-cms-label={label || keyName}
      onClickCapture={onClick}
      title={`Edit: ${label || keyName}`}
    >
      {children}
    </div>
  );
};

export default Editable;
